import React, { useCallback, useMemo, useState } from 'react';
import { Tooltip } from '../tooltip';
import clsx from 'clsx';
import { Divider } from '../divider';
import { images } from '../../constants/images';
import { BookingItem, BookingRequest, tentTypeToName, TentTypeV1 } from '@camp67/model';
import { tents } from '../../constants/tents';
import { formatDate } from 'date-fns/format';
import { hasValidDates, parseDate } from '@camp67/model/booking/dates';
import { MetadataWithIcon } from '../metadata';
import Sprite from '../../sprites/sprite';
import { displayPrice, exchangeRate } from '../../state/prices';
import { FormattedString, useMessages } from '../../i18n/useMessages';
import {
  AmplitudeEvent,
  analyticsState,
  ButtonSource,
  ButtonTag,
} from '../../state/amplitude';

type SubmissionCallToActionProps = {
  total: number;
  totalWithGst: number;
  totalFromBathingDays: number;
  totalGst: number;
  nights: number;
  hasErrors: boolean;
  canSubmit: boolean;
  onClick: VoidFunction;
  reservation: BookingRequest['reservation'];
};

export const SubmissionCallToAction: React.FC<SubmissionCallToActionProps> = (props) => (
  <div className="w-full">
    <div className="md:hidden">
      <SubmissionCallToActionMobile {...props} />
    </div>
    <div className="hidden md:block">
      <SubmissionCallToActionMedium {...props} />
    </div>
  </div>
);

export const SubmissionCallToActionMobile: React.FC<SubmissionCallToActionProps> = ({
  total,
  totalWithGst,
  nights,
  canSubmit,
  reservation,
  onClick,
}) => {
  const rooms = reservation.rooms;
  const totalRooms = rooms.reduce((sum, [, c]) => sum + c, 0);

  const isValidRange = hasValidDates(reservation.startDate, reservation.endDate);

  const noStartDate = !reservation.startDate;
  const noEndDate = !reservation.endDate;
  const startDate =
    nights && !noStartDate
      ? formatDate(parseDate(reservation.startDate), 'dd')
      : undefined;
  const endDate =
    nights && !noEndDate
      ? formatDate(parseDate(reservation.endDate), 'dd MMM')
      : undefined;

  const totalPriceWithGST = displayPrice({
    priceInINR: totalWithGst,
  });

  const getMessage = useMessages();

  const roomsLabel =
    totalRooms === 1
      ? getMessage('camp67-reservation.form.submit.room')
      : getMessage('camp67-reservation.form.submit.rooms');

  return (
    <div
      className={
        'w-full bg-white flex fixed z-[2000] bottom-0 left-0 shadow-top pl-5 h-14 items-center'
      }
    >
      {nights && isValidRange ? (
        <div>
          {total && totalRooms > 0 ? (
            <p className="font-mono font-bold text-sm">
              {totalPriceWithGST}&nbsp;
              {getMessage('camp67-reservation.form.submit.total').toLocaleLowerCase()}
            </p>
          ) : (
            ''
          )}
          <p className="font-sans text-gray-500 text-sm">
            {startDate} - {endDate}{' '}
            {total && totalRooms > 0
              ? `(${totalRooms} ${roomsLabel.toLocaleLowerCase()})`
              : ''}
          </p>
        </div>
      ) : (
        <p className="font-sans py-2 text-sm">
          {getMessage('camp67-reservation.form.submit.empty')}
        </p>
      )}

      <button
        className={clsx(
          'bg-red-200 text-white whitespace-nowrap flex flex-col flex-1 items-center justify-center py-2 px-4 ml-4 h-14',
          { 'cursor-pointer': canSubmit, 'opacity-50 cursor-auto': !canSubmit },
        )}
        type="button"
        disabled={!canSubmit}
        onClick={onClick}
      >
        <p className="font-sans uppercase text-xs xs:text-sm md:text-base font-bold tracking-widest text-left w-full">
          {getMessage('camp67.continue')}
        </p>
      </button>
    </div>
  );
};

export const SubmissionCallToActionMedium: React.FC<SubmissionCallToActionProps> = ({
  total,
  totalWithGst,
  totalFromBathingDays,
  totalGst,
  nights,
  canSubmit,
  reservation,
  onClick,
}) => {
  const rooms = reservation.rooms;
  const totalRooms = rooms.reduce((sum, [, c]) => sum + c, 0);

  const isValidRange = hasValidDates(reservation.startDate, reservation.endDate);

  const noStartDate = !reservation.startDate;
  const noEndDate = !reservation.endDate;
  const startDate =
    nights && !noStartDate
      ? formatDate(parseDate(reservation.startDate), 'dd MMM')
      : undefined;
  const endDate =
    nights && !noEndDate
      ? formatDate(parseDate(reservation.endDate), 'dd MMM')
      : undefined;

  const totalPrice = displayPrice({ priceInINR: total });
  const totalPriceWithGST = displayPrice({
    priceInINR: totalWithGst,
  });
  const totalPriceFromBathingDays = displayPrice({ priceInINR: totalFromBathingDays });
  const totalPriceGst = displayPrice({ priceInINR: totalGst });

  const getMessage = useMessages();
  let bathingDaysTooltip = `${totalPriceFromBathingDays} ${getMessage('camp67-reservation.form.submit.bathing-added')}`;
  if (totalFromBathingDays === total) {
    bathingDaysTooltip = `${totalPriceFromBathingDays} ${getMessage('camp67-reservation.form.submit.bathing-charged')}`;
  }

  const nightsLabel =
    nights === 1
      ? getMessage('camp67-reservation.form.submit.night')
      : getMessage('camp67-reservation.form.submit.nights');
  const roomsLabel =
    totalRooms === 1
      ? getMessage('camp67-reservation.form.submit.room')
      : getMessage('camp67-reservation.form.submit.rooms');

  return (
    <div
      className={
        'w-full md:mt-9 flex md:block sm:pt-6 bg-white text-center fixed z-[2000] md:z-10 bottom-0 left-0 shadow-top md:shadow-none md:sticky md:top-0 self-start rounded-lg'
      }
    >
      <div className="hidden md:flex flex-row items-center justify-center w-full">
        <Divider className="border-dashed border-gray-300" />
        <img
          src={images['logo-dark']}
          alt="Dark variant of the camp67 logo"
          className="min-w-16 mx-4 pointer-events-none select-none"
        />
        <Divider className="border-dashed border-gray-300" />
      </div>
      {nights && isValidRange ? (
        <>
          <p className="font-sans mt-3 px-12 text-gray-500">
            {startDate} - {endDate}, 2025
          </p>
          <div className="flex flex-row items-center gap-5 mt-1.5 justify-center">
            <MetadataWithIcon
              text={`${nights} ${nightsLabel}`.toLocaleLowerCase()}
              icon="nights"
            />
            <MetadataWithIcon
              text={`${totalRooms} ${roomsLabel.toLocaleLowerCase()}`}
              icon="room"
            />
          </div>
        </>
      ) : (
        <p className="font-sans mt-3 px-2">
          {getMessage('camp67-reservation.form.submit.empty')}
        </p>
      )}
      {totalRooms > 0 ? <SubmissionCTAGallery rooms={rooms} /> : null}
      {total && totalRooms > 0 ? (
        <>
          <hgroup>
            <p className="font-sans uppercase text-gray-500 mt-5 text-sm tracking-wider">
              {getMessage('camp67-reservation.form.submit.total')}
            </p>
            <Tooltip
              text={
                <>
                  {getMessage('camp67-reservation.form.submit.price-shown-in')}{' '}
                  {exchangeRate.currency}.{' '}
                  {exchangeRate.currency !== 'INR' ? (
                    <>
                      <FormattedString message="camp67-reservation.form.submit.exchange-calculated" />{' '}
                      ({exchangeRate.rate.toFixed(2)} INR = 1 {exchangeRate.currency}).
                    </>
                  ) : null}
                </>
              }
              spacing="tall"
            >
              <h3 className="font-mono text-2xl md:text-3xl font-medium">
                {totalPriceWithGST}
              </h3>
            </Tooltip>
          </hgroup>
          <Divider className="hidden md:block border-dashed border-gray-300 my-5" />
          <ul className="hidden md:block font-mono max-w-[80%] w-120 m-auto px-2">
            <li className="flex flex-row items-center w-full gap-4 justify-center relative whitespace-nowrap text-nowrap">
              <p>{getMessage('camp67-reservation.form.submit.room')}</p>
              <Divider className="border-dotted border-gray-400" />
              <div>
                <p>{totalPrice}</p>
                <Tooltip
                  text={bathingDaysTooltip}
                  className="absolute top-1/2 -translate-y-1/2 cursor-pointer -right-7 font-sans tracking-normal"
                >
                  <Sprite
                    name="info"
                    width="36"
                    height="36"
                    className="w-5 h-5 fill-green-300"
                  />
                </Tooltip>
              </div>
            </li>
            <ul className="text-left text-xs hidden md:flex flex-col whitespace-nowrap w-full text-gray-500 mt-1 m-auto">
              {rooms.map(([r, c], index) => (
                <li key={index} className="m-0.5">
                  {c} x {tentTypeToName[r]}, {nights} {nightsLabel.toLocaleLowerCase()}
                </li>
              ))}
            </ul>
            <li className="flex flex-row items-center w-full gap-4 justify-center mt-3">
              <p>GST</p>
              <Divider className="border-dotted border-gray-400" />
              <p>{totalPriceGst}</p>
            </li>
          </ul>
        </>
      ) : null}
      <button
        className={clsx(
          'bg-red-200 w-full text-white mt-5 flex flex-col items-center py-6 px-6 rounded-md shadow-mdy shadow-red-400',
          { 'cursor-pointer': canSubmit, 'opacity-50 cursor-auto': !canSubmit },
        )}
        type="button"
        disabled={!canSubmit}
        onClick={onClick}
      >
        <p className="font-sans uppercase text-sm md:text-base font-bold tracking-widest">
          {getMessage('camp67.continue')}
        </p>
      </button>
    </div>
  );
};

interface SubmissionCTAGalleryProps {
  rooms: BookingItem[];
  showOnMobile?: boolean;
  showLabels?: boolean;
  buttonSource?: ButtonSource;
  onClickRoom?: (room: TentTypeV1) => void;
}

export const SubmissionCTAGallery: React.FC<SubmissionCTAGalleryProps> = ({
  rooms,
  showOnMobile = false,
  showLabels = false,
  buttonSource,
  onClickRoom,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const totalRooms = rooms.length;
  const onNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % totalRooms);
    analyticsState.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
      buttonTag: ButtonTag.NEXT_IMAGE,
      buttonSource,
    });
  }, [buttonSource, totalRooms]);
  const onPrev = useCallback(() => {
    setActiveIndex((prev) => Math.max(0, prev - 1));
    analyticsState.amplitude?.track(AmplitudeEvent.BUTTON_CLICK, {
      buttonTag: ButtonTag.PREV_IMAGE,
      buttonSource,
    });
  }, [buttonSource]);

  const activeRoom = useMemo(
    () => (totalRooms > 0 ? tents[rooms[activeIndex][0]] : null),
    [activeIndex, rooms, totalRooms],
  );
  const getMessage = useMessages();

  if (totalRooms === 0 || !activeRoom) {
    return null;
  }

  const activeRoomTitle = getMessage(activeRoom.title);

  return (
    <div className="w-full h-full relative mt-4">
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <img
        className={clsx(
          'md:flex w-full h-full object-cover pointer-events-none select-none',
          {
            hidden: !showOnMobile,
          },
        )}
        src={activeRoom?.imageUrl}
        alt={activeRoom?.imageAlt}
        onClick={() => onClickRoom?.(activeRoom.tentType)}
      />
      {showLabels ? (
        <span className="bg-white absolute left-4 bottom-4 px-2 py-1 rounded-sm text-xs uppercase tracking-wider font-medium">
          {activeRoomTitle}
        </span>
      ) : null}
      {totalRooms > 1 ? (
        <>
          <button
            onClick={onPrev}
            type="button"
            className={clsx(
              'min-w-10 w-10 h-10 min-h-10 rounded-full bg-white absolute top-1/2 -translate-y-1/2 left-6 flex-col items-center justify-center',
              {
                hidden: !showOnMobile,
                flex: showOnMobile,
              },
            )}
          >
            <Sprite
              name="prev"
              width="12"
              height="21"
              className="w-3 h-3"
              strokeWidth={4}
            />
          </button>
          <button
            onClick={onNext}
            type="button"
            className={clsx(
              'min-w-10 w-10 h-10 min-h-10 rounded-full bg-white absolute top-1/2 -translate-y-1/2 right-4 flex-col items-center justify-center',
              {
                hidden: !showOnMobile,
                flex: showOnMobile,
              },
            )}
          >
            <Sprite
              name="next"
              width="13"
              height="21"
              className="w-3 h-3"
              strokeWidth={4}
            />
          </button>
        </>
      ) : null}
    </div>
  );
};
